import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TDiscountType } from '@app/core/dialog/dialog-discount/discount-membership-gift.type';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InitialConfigService } from '../app-config/initial-config.service';
import { IBookingAPI } from '../booking/models/booking-api.model';
import { IBookingCostValidationResult } from '../booking/models/booking-cost-validation-result.model';
import { IGiftcardRedemption } from '../gift-card/gift-card-redemption.model';
import { ISimpleDiscount } from '../payment/models/simple-discount.model';
import { RollerRecaptchaService } from '../roller-recaptcha/roller-recaptcha.service';
import { CStorageKeys } from '../storage/storage-keys.const';
import { IDiscounts } from './models/discounts.model';
import { IGiftcardRedemptionValidationResult } from './models/gift-card-redemption-validation-result.model';

@Injectable({
  providedIn: 'root'
})
export class DiscountService {
  jwtErrorCount = 0;
  readonly storageKeys = CStorageKeys;
  constructor(
    private http: HttpClient,
    private initialConfigService: InitialConfigService,
    private rollerRecaptchaService: RollerRecaptchaService
  ) {}

  /**
   * Checks the code validity along with the current booking information. Roller.API.Controllers.BookingsController.
   * @param type code type (discount, gift card, membership)
   * @param booking original details - spread to prevent reference changes
   * @param allowMultipleDiscountCodes venue setting to allow multiple codes to be redeemed at once
   * @param code discount code(s)
   */
  checkCodeValidity(
    type: TDiscountType,
    booking: IBookingAPI,
    allowMultipleDiscountCodes: boolean,
    code: string | string[],
    cVV?: string | string[]
  ): Observable<IBookingCostValidationResult> {
    const payload: IBookingAPI = {
      ...booking,
      giftcards: [...booking.giftcards],
      discounts: [...booking.discounts]
    };

    if (type === 'gift') {
      this.addGiftCardsToBookingApiPayload(payload, code, cVV);
    } else if (type === 'membership') {
      this.addMembershipsToBookingApiPayload(payload, code);
    } else {
      this.addDiscountsToBookingApiPayload(payload, code, allowMultipleDiscountCodes);
    }

    return this.checkBookingCost(payload);
  }

  /** Leverage the api to get a price check on the current order. */
  checkBookingCost(payload: IBookingAPI): Observable<IBookingCostValidationResult> {
    const api = this.initialConfigService.getBaseApiUrl();
    const url = payload.editBookingUniqueId ? '/checkout/user/booking/update/costs' : '/bookings/costs';
    const jwt = this.rollerRecaptchaService.getRecaptchaJwt();
    if (jwt.length > 0) {
      payload.recaptchaJwt = jwt;
    }

    return this.http.post<IBookingCostValidationResult>(`${api}${url}`, payload).pipe(
      catchError((errResponse: unknown) => {
        const err = <HttpErrorResponse>errResponse;
        if (err && err.status === HttpStatusCode.BadRequest && err.error.message === 'EXPIRED') {
          this.rollerRecaptchaService.setRecaptchaEnabledAutomatically();
          return throwError(() => err.error.message);
        } else if (err && err.status === HttpStatusCode.TooManyRequests) {
          return throwError(() => err.error.message);
        }

        return of({ validationMessage: 'Error' } as IBookingCostValidationResult);
      })
    );
  }

  /** Determine is a discount result is valid and should be added to the order */
  isDiscountValidFromResults(
    discounts: IDiscounts[] | undefined,
    codes: string[],
    type: TDiscountType,
    timesAlreadyApplied: number
  ): boolean {
    if (!discounts?.length) {
      return false;
    }

    /** Allow memberships to be applied directly. If invalid, a message will be displayed in cart */
    if (type === 'membership') {
      return true;
    }

    const validCodes = discounts.filter((sDiscount: ISimpleDiscount) =>
      codes.find((code: string) => sDiscount.code.toLocaleLowerCase() === code.toLocaleLowerCase())
    );

    // Checks whether the BE has removed the code from the payload, e.g. the code with MultipleUsesInBooking === false was duplicated
    if (validCodes.length < codes.length + timesAlreadyApplied) {
      return false;
    }

    return !validCodes.find((code: IDiscounts) => code.validationMessage);
  }

  /** Check the api results of the gift card validation and return wether the code check was valid.  */
  isGiftCardValidFromResults(results: IBookingCostValidationResult | undefined, code: string): boolean {
    if (!results?.giftCards || !results.giftCards.length) {
      return false;
    }

    // check giftcardErrors (string[]) - should be message including code - "Could not find giftcard for number 'asdf'"
    if (results.giftCardErrors?.some((mess: string) => mess.includes(code))) {
      return false;
    }

    const isValid = results.giftCards.some((card: IGiftcardRedemptionValidationResult) =>
      card.giftcardNumber.toLocaleLowerCase().includes(code.toLocaleLowerCase())
    );

    return isValid;
  }

  /** Checks the existing payload discounts for the same code. */
  private isCodeInDiscounts(discounts: ISimpleDiscount[], codeKey: string): boolean {
    return !!discounts.find((sDiscount: ISimpleDiscount) => sDiscount.code === codeKey);
  }

  /** Checks the existing payload giftcard for the same gift card number. */
  private isCodeInGiftCards(giftCard: IGiftcardRedemption[], gcNumber: string): boolean {
    return !!giftCard.find((gCard: IGiftcardRedemption) => gCard.giftCardNumber === gcNumber);
  }

  /**
   * Adds gift cards to the provided booking API payload. Filters gift cards that are already added.
   * Handles both array of codes or single code, and similar structure for cVV (world pay)
   * @param payload booking API
   * @param code GC number - as single or array
   * @param cVV (optional WorldPay) - as single or array
   */
  private addGiftCardsToBookingApiPayload(payload: IBookingAPI, code: string | string[], cVV?: string | string[]) {
    if (Array.isArray(code)) {
      /** Multiple codes and CVV added. Array.filter is not used to ensure the correct index is used for CVV */
      code.forEach((key: string, index: number) => {
        if (!this.isCodeInGiftCards(payload.giftcards, key)) {
          payload.giftcards.push({ giftCardNumber: key, cVV: Array.isArray(cVV) ? cVV[index] : undefined });
        }
      });
    } else if (
      !this.isCodeInGiftCards(payload.giftcards, code) &&
      (typeof cVV === 'string' || typeof cVV === 'undefined')
    ) {
      /** Single gift card code and CVV (or none) */
      payload.giftcards.push({ giftCardNumber: code, cVV });
    }
  }

  /**
   * Adds discount codes to the provided booking API payload. .
   * Handles as an array of codes and filters codes that are already added. When only single discounts allowed, the first valid code is used
   * @param payload booking API
   * @param code discount code - as single or array
   * @param allowMultipleDiscountCodes true if multiple codes can be redeemed
   * @returns true if the code was added successfully, false if it was already added
   */
  private addDiscountsToBookingApiPayload(
    payload: IBookingAPI,
    code: string | string[],
    allowMultipleDiscountCodes: boolean
  ): boolean {
    const codes = [code].flat();
    if (allowMultipleDiscountCodes) {
      codes.forEach((key: string) => {
        payload.discounts = [...payload.discounts, { code: key }];
      });
    } else {
      payload.discounts = [{ code: codes[0] }];
    }

    return true;
  }

  /**
   * Adds a applied membership code to the provided booking API payload discount array
   * @param payload booking API / existing booking
   * @param code membership code - as single or array
   */
  private addMembershipsToBookingApiPayload(booking: IBookingAPI, code: string | string[]): void {
    const validCodes = [code].flat().filter((key: string) => !this.isCodeInDiscounts(booking.discounts, key));

    if (validCodes?.length) {
      booking.discounts = [...booking.discounts, ...validCodes.map((key: string) => ({ code: key }))];
    }
  }
}
